import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiGet, apiPatch } from '../../lib/api-client';
import { CardPair, Card } from '../shared/Card';
import { MATRIX_CELLS } from '@range-reader/poker-core';
import type { SessionRecord, HandRecord, RangeSnapshot, MatrixCellStatus } from '@range-reader/shared';

interface SessionDetailViewProps {
  sessionId: string;
  onBack: () => void;
}

const STREET_COLORS: Record<string, string> = {
  preflop: '#6366f1', flop: '#10b981', turn: '#f59e0b', river: '#ef4444',
};

const CELL_SIZE = 6;
const GRID_PX = 13 * CELL_SIZE;

function MiniGrid({ snapshot, prevSnapshot }: { snapshot: RangeSnapshot; prevSnapshot?: RangeSnapshot }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const highlighted = new Set(snapshot.highlightedCategories);
    const eliminated = new Set(
      snapshot.matrixState
        .map((s: MatrixCellStatus, i: number) => s === 'ELIMINATED' ? (MATRIX_CELLS[i]?.category ?? '') : '')
        .filter(Boolean)
    );
    const prevEliminated = prevSnapshot
      ? new Set(prevSnapshot.matrixState
          .map((s: MatrixCellStatus, i: number) => s === 'ELIMINATED' ? (MATRIX_CELLS[i]?.category ?? '') : '')
          .filter(Boolean))
      : new Set<string>();
    const newlyEliminated = new Set([...eliminated].filter(h => !prevEliminated.has(h)));

    ctx.clearRect(0, 0, GRID_PX, GRID_PX);
    MATRIX_CELLS.forEach((cell, idx) => {
      const row = Math.floor(idx / 13);
      const col = idx % 13;
      const x = col * CELL_SIZE;
      const y = row * CELL_SIZE;
      const cat = cell.category;

      let color: string;
      if (eliminated.has(cat)) {
        color = newlyEliminated.has(cat) ? '#7f1d1d' : '#1f2937';
      } else if (highlighted.has(cat)) {
        color = '#2563eb';
      } else {
        color = '#374151';
      }

      ctx.fillStyle = color;
      ctx.fillRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    });
  }, [snapshot, prevSnapshot]);

  return <canvas ref={canvasRef} width={GRID_PX} height={GRID_PX} className="detail-mini-grid" />;
}

function LargeGrid({ snapshot, actualHand }: { snapshot: RangeSnapshot; actualHand: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const SIZE = 10;
  const PX = 13 * SIZE;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const highlighted = new Set(snapshot.highlightedCategories);
    const eliminated = new Set(
      snapshot.matrixState
        .map((s: MatrixCellStatus, i: number) => s === 'ELIMINATED' ? (MATRIX_CELLS[i]?.category ?? '') : '')
        .filter(Boolean)
    );

    ctx.clearRect(0, 0, PX, PX);
    MATRIX_CELLS.forEach((cell, idx) => {
      const row = Math.floor(idx / 13);
      const col = idx % 13;
      const x = col * SIZE;
      const y = row * SIZE;
      const cat = cell.category;

      let color: string;
      if (cat === actualHand) {
        color = '#fbbf24'; // gold for actual hand
      } else if (eliminated.has(cat)) {
        color = '#1f2937';
      } else if (highlighted.has(cat)) {
        color = '#2563eb';
      } else {
        color = '#374151';
      }

      ctx.fillStyle = color;
      ctx.fillRect(x + 1, y + 1, SIZE - 2, SIZE - 2);
    });
  }, [snapshot, actualHand, PX, SIZE]);

  return <canvas ref={canvasRef} width={PX} height={PX} className="detail-large-grid" />;
}

function ScoreBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="score-bar-track">
      <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function formatDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function SessionDetailView({ sessionId, onBack }: SessionDetailViewProps) {
  const [session, setSession] = useState<SessionRecord | null>(null);
  const [hand, setHand] = useState<HandRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [selectedSnap, setSelectedSnap] = useState(0);

  useEffect(() => {
    setLoading(true);
    apiGet<{ session: SessionRecord; hand: HandRecord }>(`/api/sessions/${sessionId}`)
      .then(data => {
        setSession(data.session);
        setHand(data.hand);
        setNote(data.session.note ?? '');
        if (data.session.snapshots.length > 0) {
          setSelectedSnap(data.session.snapshots.length - 1);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sessionId]);

  const handleSaveNote = useCallback(() => {
    if (!note.trim()) return;
    apiPatch(`/api/sessions/${sessionId}/note`, { note: note.trim() }).catch(console.error);
    setNoteSaved(true);
  }, [sessionId, note]);

  if (loading) {
    return (
      <div className="detail-screen">
        <div className="detail-loading">Loading session...</div>
      </div>
    );
  }

  if (!session || !hand) {
    return (
      <div className="detail-screen">
        <div className="detail-error">
          <p>Session not found.</p>
          <button className="detail-back-btn" onClick={onBack}>Back to History</button>
        </div>
      </div>
    );
  }

  const { hit, baseScore, nearMissCredit, streetBonus, isolationStreet, accuracyScore } = session;
  const scoreColor = accuracyScore >= 70 ? '#10b981' : accuracyScore >= 40 ? '#f59e0b' : '#ef4444';
  const communityCards = hand.communityCards.split(' ').filter(Boolean);
  const snapshots = session.snapshots;
  const currentSnap = snapshots[selectedSnap] ?? null;

  // Group eliminations by street
  const elimByStreet: Record<string, string[]> = {};
  for (const e of session.eliminationLog) {
    if (!elimByStreet[e.street]) elimByStreet[e.street] = [];
    elimByStreet[e.street].push(e.handLabel);
  }

  return (
    <div className="detail-screen">
      {/* Header */}
      <div className="detail-header">
        <button className="detail-back-btn" onClick={onBack}>&larr; Back to History</button>
        <span className="detail-date">{formatDate(session.playedAt)}</span>
      </div>

      <div className="detail-body">
        {/* LEFT column: hand info + range timeline */}
        <div className="detail-left">
          {/* Hand info card */}
          <div className="detail-hand-card">
            <div className="detail-hand-header">
              <span className="detail-hand-title">Hand #{hand.handIdRaw}</span>
              <span className="detail-stakes">{(hand.bigBlind / 100).toFixed(2)} BB / {hand.tableType}-max</span>
            </div>

            <div className="detail-winner-row">
              <span className="detail-winner-label">{hand.winner.name} held:</span>
              <CardPair cards={hand.winner.holeCards} />
              <span className="detail-hand-desc">{hand.winner.handDesc}</span>
            </div>

            {communityCards.length > 0 && (
              <div className="detail-board-row">
                <span className="detail-board-label">Board:</span>
                <div className="detail-board-cards">
                  {communityCards.map((c, i) => <Card key={i} card={c} small />)}
                </div>
              </div>
            )}
          </div>

          {/* Range evolution timeline */}
          <div className="detail-timeline">
            <h3>Range Evolution</h3>
            <div className="detail-snap-list">
              {snapshots.map((snap, i) => {
                const prevSnap = i > 0 ? snapshots[i - 1] : undefined;
                const prevCount = prevSnap ? prevSnap.highlightedCategories.length : (session.matrixDefault === 'full' ? 169 : 0);
                const delta = snap.highlightedCategories.length - prevCount;
                return (
                  <div
                    key={i}
                    className={`detail-snap-item ${selectedSnap === i ? 'selected' : ''}`}
                    onClick={() => setSelectedSnap(i)}
                  >
                    <MiniGrid snapshot={snap} prevSnapshot={prevSnap} />
                    <div className="detail-snap-meta">
                      <span
                        className="detail-snap-street"
                        style={{ color: STREET_COLORS[snap.street] ?? '#9ca3af' }}
                      >
                        {snap.street.toUpperCase()}
                      </span>
                      <span className="detail-snap-trigger">{snap.triggerLabel}</span>
                      <span className="detail-snap-width">
                        {snap.highlightedCategories.length} hands
                        {delta !== 0 && i > 0 && (
                          <span className={delta < 0 ? 'delta-neg' : 'delta-pos'}>
                            {delta > 0 ? '+' : ''}{delta}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Elimination log */}
          {Object.keys(elimByStreet).length > 0 && (
            <div className="detail-elim-log">
              <h3>Eliminations</h3>
              {(['preflop', 'flop', 'turn', 'river'] as const).map(street => {
                const hands = elimByStreet[street];
                if (!hands || hands.length === 0) return null;
                return (
                  <div key={street} className="detail-elim-street">
                    <span className="detail-elim-street-label" style={{ color: STREET_COLORS[street] }}>
                      {street.toUpperCase()}
                    </span>
                    <span className="detail-elim-hands">{hands.join(', ')}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT column: score + selected snapshot matrix */}
        <div className="detail-right">
          {/* Score card */}
          <div className="detail-score-card">
            <div className={`detail-hit-indicator ${hit ? 'hit' : 'miss'}`}>
              {hit ? 'HIT' : 'MISS'} — {session.actualHand}
            </div>

            <div className="detail-score-total" style={{ color: scoreColor }}>
              {accuracyScore.toFixed(1)} / 100
            </div>

            <div className="detail-score-rows">
              <div className="detail-score-row">
                <span>Base Score</span>
                <span>{baseScore.toFixed(1)} / 65</span>
                <ScoreBar value={baseScore} max={65} color="#3b82f6" />
              </div>
              <div className="detail-score-row">
                <span>Near-Miss</span>
                <span>{nearMissCredit} / 10</span>
                <ScoreBar value={nearMissCredit} max={10} color="#8b5cf6" />
              </div>
              <div className="detail-score-row">
                <span>Street Bonus</span>
                <span>{streetBonus} / 25</span>
                <ScoreBar value={streetBonus} max={25} color="#f59e0b" />
              </div>
            </div>

            {isolationStreet && (
              <div className="detail-isolation">
                Isolated on <span style={{ color: STREET_COLORS[isolationStreet] }}>
                  {isolationStreet.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Selected snapshot matrix */}
          {currentSnap && (
            <div className="detail-matrix-section">
              <h3>
                Range at: {currentSnap.triggerLabel}
                <span className="detail-matrix-count">
                  ({currentSnap.highlightedCategories.length} hands)
                </span>
              </h3>
              <LargeGrid snapshot={currentSnap} actualHand={session.actualHand} />
              <div className="detail-matrix-legend">
                <span className="legend-highlighted">Highlighted</span>
                <span className="legend-eliminated">Eliminated</span>
                <span className="legend-actual">Actual Hand</span>
              </div>
            </div>
          )}

          {/* Note */}
          <div className="detail-note-area">
            <textarea
              className="detail-note-input"
              placeholder="Add a note about this session..."
              value={note}
              onChange={e => { setNote(e.target.value); setNoteSaved(false); }}
              rows={2}
            />
            {note.trim() && (
              <button className="detail-note-save" onClick={handleSaveNote}>
                {noteSaved ? 'Saved' : 'Save Note'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
